import type { HexColor } from './types'

/**
 * Conversão sRGB ↔ OKLab ↔ OKLCH e contraste WCAG.
 *
 * Por que OKLCH e não a mistura em sRGB do `getShade`: em sRGB o mesmo fator de
 * mistura entrega lightness percebida com até ~25 pontos de diferença em L*
 * conforme o matiz, e escurecer desvia o matiz — verde vira oliva, âmbar vira
 * marrom. Em OKLCH os três eixos são perceptualmente independentes, então o
 * mesmo L entrega o mesmo peso visual em qualquer matiz e escurecer preserva a
 * cor. É isso que permite um único conjunto de alvos servir todos os eventos.
 *
 * O ganho decisivo, porém, é outro: com L independente, contraste vira função
 * monotônica de L. Isso transforma "qual tom deste matiz atinge 4.6:1" de chute
 * calibrado à mão em busca binária — ver `resolveBrandText`.
 *
 * Sem dependência externa por escolha: `culori` custaria ~5KB para as mesmas
 * matrizes, e uma dependência nova dentro de um contrato de design system vale
 * menos que ~120 linhas cobertas por teste.
 *
 * Matrizes de Björn Ottosson (https://bottosson.github.io/posts/oklab/).
 */

export interface Oklch {
  /** Lightness perceptual, 0 (preto) a 1 (branco). */
  l: number
  /** Croma. 0 é acromático; satura por volta de 0.37 no gamut sRGB. */
  c: number
  /** Matiz em graus, [0, 360). */
  h: number
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v))

/** sRGB com gamma → linear. O joelho em 0.04045 é da própria spec do sRGB. */
const gammaToLinear = (channel: number): number =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4

const linearToGamma = (channel: number): number =>
  channel <= 0.0031308
    ? channel * 12.92
    : 1.055 * channel ** (1 / 2.4) - 0.055

const parseChannels = (hex: HexColor): [number, number, number] => {
  const raw = hex.startsWith('#') ? hex.slice(1) : hex
  return [
    parseInt(raw.slice(0, 2), 16) / 255,
    parseInt(raw.slice(2, 4), 16) / 255,
    parseInt(raw.slice(4, 6), 16) / 255
  ]
}

const toHexChannel = (channel: number): string =>
  Math.round(clamp01(channel) * 255)
    .toString(16)
    .padStart(2, '0')

/**
 * Luminância relativa WCAG 2.x. É a base de `contrastRatio` e, por tabela,
 * dos números que `ACCESSIBILITY.md §1` afirma cumprir.
 */
export const relativeLuminance = (hex: HexColor): number => {
  const [r, g, b] = parseChannels(hex).map(gammaToLinear)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Razão de contraste WCAG entre duas cores. Simétrica, de 1:1 a 21:1. */
export const contrastRatio = (a: HexColor, b: HexColor): number => {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [claro, escuro] = la >= lb ? [la, lb] : [lb, la]
  return (claro + 0.05) / (escuro + 0.05)
}

export const hexToOklch = (hex: HexColor): Oklch => {
  const [r, g, b] = parseChannels(hex).map(gammaToLinear)

  // Linear sRGB → LMS cone response, depois a raiz cúbica que dá a
  // uniformidade perceptual do espaço.
  const lCone = Math.cbrt(
    0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b
  )
  const mCone = Math.cbrt(
    0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b
  )
  const sCone = Math.cbrt(
    0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b
  )

  const l = 0.2104542553 * lCone + 0.793617785 * mCone - 0.0040720468 * sCone
  const a = 1.9779984951 * lCone - 2.428592205 * mCone + 0.4505937099 * sCone
  const bAxis =
    0.0259040371 * lCone + 0.7827717662 * mCone - 0.808675766 * sCone

  const c = Math.hypot(a, bAxis)
  // Acromático não tem matiz definido; 0 é convenção, e a croma nula garante
  // que nenhum consumidor use esse valor para nada.
  const h = c < 1e-6 ? 0 : ((Math.atan2(bAxis, a) * 180) / Math.PI + 360) % 360

  return { l, c, h }
}

/**
 * OKLCH → sRGB linear, sem clamp e sem quantização.
 *
 * É a forma crua de propósito: canal fora de [0,1] é justamente o sinal de que
 * a cor não cabe em sRGB, e é assim que o gamut mapping decide. Conferir isso
 * depois de passar por hex de 8 bits não funciona — o erro de quantização se
 * confunde com estouro de gamut e derruba cores perfeitamente válidas.
 */
export const oklchToLinearRgb = ({
  l,
  c,
  h
}: Oklch): [number, number, number] => {
  const rad = (h * Math.PI) / 180
  const a = c * Math.cos(rad)
  const bAxis = c * Math.sin(rad)

  const lCone = (l + 0.3963377774 * a + 0.2158037573 * bAxis) ** 3
  const mCone = (l - 0.1055613458 * a - 0.0638541728 * bAxis) ** 3
  const sCone = (l - 0.0894841775 * a - 1.291485548 * bAxis) ** 3

  return [
    4.0767416621 * lCone - 3.3077115913 * mCone + 0.2309699292 * sCone,
    -1.2684380046 * lCone + 2.6097574011 * mCone - 0.3413193965 * sCone,
    -0.0041960863 * lCone - 0.7034186147 * mCone + 1.707614701 * sCone
  ]
}

/**
 * OKLCH → hex. Faz clamp por canal: um par (L, C) fora do gamut sRGB produz
 * canal fora de [0,1] e sairia com a cor errada. Quem precisa do tom correto
 * dentro do gamut usa o gamut mapping (reduz C preservando L e H) em vez de
 * confiar neste clamp, que é rede de segurança e não estratégia.
 */
export const oklchToHex = (cor: Oklch): HexColor => {
  const [r, g, b] = oklchToLinearRgb(cor)

  return `#${toHexChannel(linearToGamma(r))}${toHexChannel(
    linearToGamma(g)
  )}${toHexChannel(linearToGamma(b))}`
}
